import { useState, useEffect, useMemo } from 'react';
import { Download, AlertCircle, BookmarkPlus } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { decodePaletteFromShare } from '../utils/paletteCode';

export default function LoadPaletteModal({ isOpen, onClose, onLoadPalette, onSaveRequest }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [paletteCode, setPaletteCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const livePreviewColors = useMemo(() => {
    if (!paletteCode.trim()) return [];
    try {
      const decoded = decodePaletteFromShare(paletteCode.trim());
      return decoded && decoded.length > 0 ? decoded.map(m => m.hex) : [];
    } catch {
      return [];
    }
  }, [paletteCode]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLoad = async () => {
    if (!paletteCode.trim()) {
      setError(t('savedPalettes.errors.codeRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const decodedPalette = decodePaletteFromShare(paletteCode.trim());

      if (!decodedPalette || decodedPalette.length === 0) {
        setError(t('savedPalettes.errors.invalidCode'));
        return;
      }

      // Call parent handler to load the palette
      onLoadPalette(decodedPalette);

      // Close modal and reset
      setPaletteCode('');
      onClose();
    } catch (err) {
      console.error('Failed to decode palette:', err);
      setError(t('savedPalettes.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRequest = async () => {
    if (!paletteCode.trim()) {
      setError(t('savedPalettes.errors.codeRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const decodedPalette = decodePaletteFromShare(paletteCode.trim());

      if (!decodedPalette || decodedPalette.length === 0) {
        setError(t('savedPalettes.errors.invalidCode'));
        return;
      }

      onSaveRequest(decodedPalette);

      setPaletteCode('');
      onClose();
    } catch (err) {
      console.error('Failed to decode palette:', err);
      setError(t('savedPalettes.errors.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly">
      <div
        className="absolute inset-0 z-0"
        onClick={onClose}
      />
      <div className="relative z-10 bg-white/95 backdrop-blur-md rounded-2xl max-w-md w-full p-6 shadow-soft-xl border border-rose-pastel-100/50 animate-slideUp">
        {/* Header */}
        {/* <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-800 text-center w-full">
            {t('savedPalettes.loadModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9  text-slate-600 hover:text-slate-800 flex items-center justify-center"
            disabled={isLoading}
          >
            ✕
          </button>
        </div> */}
        <div className="mb-4 lg:mb-6">
          <h3 className="text-lg lg:text-xl font-display font-semibold text-slate-800 mb-2">{t('savedPalettes.loadModal.title')}</h3>
          <div className="w-16 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full"></div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9  text-slate-600 hover:text-slate-800 flex items-center justify-center"
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('savedPalettes.loadModal.codeLabel')}
          </label>
          <div className="relative">
            <textarea
              value={paletteCode}
              onChange={(e) => {
                setPaletteCode(e.target.value);
                setError('');
              }}
              placeholder={t('savedPalettes.loadModal.codePlaceholder')}
              rows={3}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-pastel-400 focus:border-transparent resize-none font-mono text-sm bg-white/80 backdrop-blur-sm transition-all"
              disabled={isLoading}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey && !isLoading) {
                  handleLoad();
                }
              }}
            />
          </div>
          {/* Live hex preview bar */}
          {livePreviewColors.length > 0 && (
            <div className="flex rounded-xl overflow-hidden h-6 mt-3 shadow-sm">
              {livePreviewColors.map((hex, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: hex }} title={hex} />
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 flex-wrap">
          {/* <button
            onClick={onClose}
            className="flex-1 px-4 py-3 text-xs rounded-xl bg-white/80 hover:bg-white border border-slate-200 text-slate-700 font-medium transition-all"
            disabled={isLoading}
          >
            {t('savedPalettes.buttons.cancel')}
          </button> */}
          {onSaveRequest && user && (
            <button
              onClick={handleSaveRequest}
              className="flex-1 px-3 py-2 text-xs rounded-xl bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-white font-medium shadow-soft transition-all inline-flex items-center justify-center gap-2"
              disabled={isLoading || !paletteCode.trim()}
            >
              <BookmarkPlus size={13} />
              Save Palette
            </button>
          )}
          <button
            onClick={handleLoad}
            className="flex-1 px-3 py-2 text-xs rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-medium shadow-soft transition-all inline-flex items-center justify-center gap-2"
            disabled={isLoading || !paletteCode.trim()}
          >
            <Download size={13} />
            {isLoading ? t('savedPalettes.loadModal.loading') : t('savedPalettes.loadModal.loadButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
